import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import interiorCatalogApi from "../services/interiorCatalogApi";
import ItemComponentsEditor from "../components/catalog/ItemComponentsEditor";
import { parseProductDetailsText } from "../utils/parseProductDetailsText";

function normalizeList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  return [];
}

const PUBLIC_BASE_URL = "https://modularobjects.com";

function resolveFileUrl(file) {
  const raw = file?.public_url || file?.file_path || "";

  if (!raw) return "";

  const str = String(raw).trim();

  // Ispravi stare/pogrešne apsolutne URL-ove bez /mailserver
  if (str.startsWith("https://modularobjects.com/uploads/")) {
    return encodeURI(
      str.replace(
        "https://modularobjects.com/uploads/",
        "https://modularobjects.com/mailserver/uploads/"
      )
    );
  }

  if (str.startsWith("http://modularobjects.com/uploads/")) {
    return encodeURI(
      str.replace(
        "http://modularobjects.com/uploads/",
        "https://modularobjects.com/mailserver/uploads/"
      )
    );
  }

  // Ako je već dobar full URL
  if (str.startsWith("http")) {
    return encodeURI(str);
  }

  // Ako je već dobra relativna putanja
  if (str.startsWith("/mailserver/")) {
    return encodeURI(`${PUBLIC_BASE_URL}${str}`);
  }

  // Ako backend vrati /uploads/... dodaj /mailserver
  if (str.startsWith("/uploads/")) {
    return encodeURI(`${PUBLIC_BASE_URL}/mailserver${str}`);
  }

  if (str.startsWith("/")) {
    return encodeURI(`${PUBLIC_BASE_URL}${str}`);
  }

  return encodeURI(`${PUBLIC_BASE_URL}/mailserver/${str}`);
}

function ItemFilesPanel({
  itemFiles,
  fileRole,
  setFileRole,
  fileLabel,
  setFileLabel,
  uploadingFile,
  onUpload,
  onDelete,
}) {
  return (
    <div className="border rounded bg-white p-3 space-y-3">
      <div>
        <div className="font-semibold text-sm text-slate-800">
          Files / Documents
        </div>
        <div className="text-xs text-slate-500">
          Product images, PDF details, installation manuals and technical documents.
        </div>
      </div>

      <div className="border rounded bg-slate-50 p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              File role
            </label>
            <select
              value={fileRole}
              onChange={(e) => setFileRole(e.target.value)}
              className="w-full px-2 py-2 border rounded text-sm bg-white"
            >
              <option value="product_image">Product image</option>
              <option value="product_details_pdf">Product details PDF</option>
              <option value="installation_manual">Installation manual</option>
              <option value="technical_drawing">Technical drawing</option>
              <option value="drilling_template">Drilling template</option>
              <option value="certificate">Certificate</option>
              <option value="datasheet">Datasheet</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Label
            </label>
            <input
              value={fileLabel}
              onChange={(e) => setFileLabel(e.target.value)}
              placeholder="e.g. Blum product details"
              className="w-full px-2 py-2 border rounded text-sm"
            />
          </div>
        </div>

        <label className="block">
          <span className="block text-xs text-slate-500 mb-1">
            Upload file
          </span>
          <input
            type="file"
            onChange={onUpload}
            disabled={uploadingFile}
            className="w-full text-sm"
            accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx"
          />
        </label>

        {uploadingFile ? (
          <div className="text-xs text-blue-600">Uploading...</div>
        ) : null}
      </div>

      <div className="space-y-2">
        {itemFiles.length ? (
          itemFiles.map((file) => {
            const url = resolveFileUrl(file);
            const isImage = String(file.mime_type || "").startsWith("image/");

            return (
              <div
                key={file.id}
                className="border rounded p-2 flex gap-3 bg-white hover:bg-slate-50"
              >
                <div className="w-16 h-16 shrink-0 rounded border bg-slate-100 overflow-hidden flex items-center justify-center">
                  {isImage && url ? (
  <img
    src={url}
    alt={file.file_label || file.original_filename}
    className="w-full h-full object-cover"
    onError={(e) => {
      e.currentTarget.style.display = "none";
    }}
  />
) : (
  <div className="text-[10px] text-slate-500 uppercase">
    {file.file_ext || "file"}
  </div>
)}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {file.file_label || file.original_filename}
                  </div>

                  <div className="text-xs text-slate-500 truncate">
                    {file.original_filename}
                  </div>

                  <div className="text-[11px] text-slate-400 mt-1">
                    {file.file_role} · {Math.round((file.file_size || 0) / 1024)} KB
                  </div>

                  <div className="flex gap-2 mt-2">
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 border rounded text-xs bg-white text-blue-700 hover:bg-blue-50"
                      >
                        Open
                      </a>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => onDelete(file.id)}
                      className="px-2 py-1 border rounded text-xs bg-white text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-sm text-slate-400 border rounded p-3 bg-slate-50">
            No files uploaded for this item.
          </div>
        )}
      </div>
    </div>
  );
}

export default function InteriorItemAssemblyEditorPage() {
  const navigate = useNavigate();
  const { itemId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [item, setItem] = useState(null);
  const [components, setComponents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetSearch, setAssetSearch] = useState("");
  const [errorText, setErrorText] = useState("");

  const [componentCategories, setComponentCategories] = useState([]);
const [parameterDefinitions, setParameterDefinitions] = useState([]);

const [showProductImport, setShowProductImport] = useState(false);
const [productImportText, setProductImportText] = useState("");
const [parsedProductData, setParsedProductData] = useState(null);

const [itemFiles, setItemFiles] = useState([]);
const [fileRole, setFileRole] = useState("product_details_pdf");
const [fileLabel, setFileLabel] = useState("");
const [uploadingFile, setUploadingFile] = useState(false);
const [showFilesPanel, setShowFilesPanel] = useState(false);
const [itemMounting, setItemMounting] = useState(null);
const [itemMountingItems, setItemMountingItems] = useState([]);

const [componentItems, setComponentItems] = useState([]);

  async function load() {
    try {
      setLoading(true);
      setErrorText("");

  const [
  itemRes,
  componentsRes,
  assetsRes,
  componentItemsRes,
  categoriesRes,
  parameterDefsRes,
  filesRes,
  itemMountingRes,
] = await Promise.all([
  interiorCatalogApi.getFamilyItemById(itemId),
  interiorCatalogApi.listItemAssets(itemId),
  interiorCatalogApi.listAssets({ limit: 100, offset: 0 }),
  interiorCatalogApi.listFamilyItems({
    search: "",
    status: "active",
    limit: 200,
    offset: 0,
  }),
  interiorCatalogApi.listCategories({ scope: "component" }),
  interiorCatalogApi.listParameterDefinitions({ scope: "item_component" }),
  interiorCatalogApi.listItemFiles(itemId),
  interiorCatalogApi.getItemMountingByItemId(itemId),
]);

const nextItemMounting = itemMountingRes?.item || null;

let nextItemMountingItems = [];

if (nextItemMounting?.id) {
  try {
    const mountingItemsRes =
      await interiorCatalogApi.listItemMountingItems(nextItemMounting.id);

    nextItemMountingItems = mountingItemsRes?.items || [];
  } catch (err) {
    console.warn("load item mounting items error:", err);
  }
}

const rawComponents = componentsRes?.items || [];

const enrichedComponents = await Promise.all(
  rawComponents.map(async (component) => {
    let nextAssetMounting = component.asset_mounting || null;
    let nextAssetMountingItems = [];

    if (nextAssetMounting?.id) {
      try {
        const assetMountingItemsRes =
          await interiorCatalogApi.listAssetMountingItems(nextAssetMounting.id);

        nextAssetMountingItems = assetMountingItemsRes?.items || [];
      } catch (err) {
        console.warn("load asset mounting items for component error:", {
          componentId: component.id,
          assetMountingId: nextAssetMounting.id,
          err,
        });
      }
    }

    let nextComponentItemMounting =
      component.component_item?.item_mounting ||
      component.component_item?.itemMounting ||
      component.item_mounting ||
      null;

    if (!nextComponentItemMounting && component.component_item_id) {
      try {
        const componentItemMountingRes =
          await interiorCatalogApi.getItemMountingByItemId(
            component.component_item_id
          );

        nextComponentItemMounting =
          componentItemMountingRes?.item || null;
      } catch (err) {
        console.warn("load component item mounting error:", {
          componentId: component.id,
          componentItemId: component.component_item_id,
          err,
        });
      }
    }

    return {
      ...component,

      asset_mounting: nextAssetMounting
        ? {
            ...nextAssetMounting,
            items: nextAssetMountingItems,
          }
        : null,

      component_item: component.component_item
        ? {
            ...component.component_item,
            item_mounting: nextComponentItemMounting,
          }
        : component.component_item,

      item_mounting: nextComponentItemMounting,
    };
  })
);

setItem(itemRes?.item || itemRes);
setComponents(enrichedComponents);
setAssets(normalizeList(assetsRes));
setComponentItems(componentItemsRes?.items || []);
setComponentCategories(categoriesRes?.items || []);
setItemFiles(filesRes?.items || []);
setParameterDefinitions(parameterDefsRes?.items || []);
setItemMounting(nextItemMounting);
setItemMountingItems(nextItemMountingItems);
    } catch (err) {
      console.error("load assembly editor error:", err);
      setErrorText(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load assembly editor."
      );
    } finally {
      setLoading(false);
    }
  }

  async function saveItemPatch(patch) {
  if (!item?.id) return;

  const payload = {
    ...item,
    ...patch,
  };

  await interiorCatalogApi.updateFamilyItem(item.id, payload);

  const refreshed = await interiorCatalogApi.getFamilyItemById(item.id);
  setItem(refreshed?.item || refreshed);
}

function parseProductImport() {
  const parsed = parseProductDetailsText(productImportText);
  setParsedProductData(parsed);
}

async function reloadItemFiles() {
  const res = await interiorCatalogApi.listItemFiles(itemId);
  setItemFiles(res.items || []);
}

async function handleUploadItemFile(event) {
  const file = event.target.files?.[0];
  if (!file || !item?.id) return;

  try {
    setUploadingFile(true);

    await interiorCatalogApi.uploadItemFile(item.id, {
      file,
      file_role: fileRole,
      file_label: fileLabel,
    });

    setFileLabel("");
    event.target.value = "";

    await reloadItemFiles();
  } catch (err) {
    console.error("upload item file error:", err);
    alert(
      err?.response?.data?.message ||
        err?.message ||
        "Failed to upload item file."
    );
  } finally {
    setUploadingFile(false);
  }
}

async function deleteItemFile(fileId) {
  if (!item?.id || !fileId) return;

  if (!window.confirm("Delete this item file?")) return;

  try {
    await interiorCatalogApi.deleteItemFile(item.id, fileId);
    await reloadItemFiles();
  } catch (err) {
    console.error("delete item file error:", err);
    alert(
      err?.response?.data?.message ||
        err?.message ||
        "Failed to delete file."
    );
  }
}

async function applyProductImport() {
  if (!item?.id || !parsedProductData) return;

  const parsedCode = String(parsedProductData.code || "").trim();
  const currentCode = String(item.code || "").trim();

  const codeIsDifferent =
    parsedCode &&
    currentCode &&
    parsedCode.toLowerCase() !== currentCode.toLowerCase();

  if (codeIsDifferent) {
    const ok = window.confirm(
      `Parsed product code is "${parsedCode}", but current item code is "${currentCode}".\n\n` +
        `This may mean you are importing details into the wrong item.\n\n` +
        `Apply data WITHOUT changing current item code?`
    );

    if (!ok) return;
  }

  const technical = parsedProductData.attributes_json?.technical || {};

const runnerLength =
  Number(technical.nominalLengthMm) ||
  Number(technical.drawerLengthMm) ||
  300;

const drawerDepth =
  Number(technical.drawerLengthMm) ||
  Number(runnerLength) ||
  300;

const runnerHeight =
  Number(technical.shankHeightOfCabinetProfileMm) ||
  40;

const minimumCabinetDepth =
  Number(technical.minimumInternalCabinetDepthMm) ||
  null;

const drawerDepthReduction =
  Math.max(0, runnerLength - drawerDepth) || 10;

  await saveItemPatch({
    code: codeIsDifferent
      ? item.code
      : parsedProductData.code || item.code,
    sku: parsedProductData.sku || item.sku,
    name: parsedProductData.name || item.name,
    brand: parsedProductData.brand || item.brand,
    manufacturer: parsedProductData.manufacturer || item.manufacturer,
    finish: parsedProductData.finish || item.finish,
    attributes_json: {
  ...(item.attributes_json || {}),
  ...(parsedProductData.attributes_json || {}),

  assembly_parameters: {
    ...(item.attributes_json?.assembly_parameters || {}),
    runnerLength,
    drawerDepth,
    runnerHeight,
    minimumCabinetDepth,
    drawerDepthReduction,

    sideClearance:
      Number(item.attributes_json?.assembly_parameters?.sideClearance) ||
      7.5,
  },

  import_warning: codeIsDifferent
    ? {
        parsedCode,
        currentCode,
        message:
          "Imported product details had a different code than current item. Item code was not changed.",
      }
    : undefined,
},
  });

  setShowProductImport(false);
  setProductImportText("");
  setParsedProductData(null);
}

  async function searchAssets(searchText = "") {
  const res = await interiorCatalogApi.listFamilyItems({
    search: String(searchText || "").trim(),
    status: "active",
    limit: 100,
    offset: 0,
  });

  setComponentItems(res.items || []);
}

  useEffect(() => {
    load();
  }, [itemId]);

  useEffect(() => {
    const t = setTimeout(() => {
      searchAssets(assetSearch);
    }, 350);

    return () => clearTimeout(t);
  }, [assetSearch]);

  async function reloadComponents() {
    const res = await interiorCatalogApi.listItemAssets(itemId);
    setComponents(res.items || []);
  }

  if (loading) {
    return (
      <div className="h-[calc(100vh-80px)] flex items-center justify-center text-slate-500">
        Loading item assembly editor...
      </div>
    );
  }

  if (!item) {
    return (
      <div className="h-[calc(100vh-80px)] flex items-center justify-center text-red-600">
        {errorText || "Item not found."}
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] overflow-hidden flex flex-col bg-slate-100">
      <div className="h-14 shrink-0 border-b bg-white px-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Item Assembly Editor
          </div>
          <div className="text-sm font-semibold truncate text-slate-800">
            {item.name || "Item"}
          </div>
          <div className="text-xs text-slate-500 truncate">
            item #{item.id} · {item.code}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/interior-editor/catalog")}
            className="px-3 py-1.5 rounded border bg-white text-sm hover:bg-slate-50"
          >
            Catalog
          </button>

          <button
            type="button"
            onClick={() =>
              navigate(`/interior-editor/catalog/items/${item.id}/mounting`)
            }
            className="px-3 py-1.5 rounded border bg-white text-sm hover:bg-slate-50"
          >
            Mounting
          </button>

          <button
  type="button"
  onClick={() => setShowProductImport(true)}
  className="px-3 py-1.5 rounded border bg-white text-sm hover:bg-slate-50"
>
  Import Product Details
</button>

<button
  type="button"
  onClick={() => setShowFilesPanel(true)}
  className="px-3 py-1.5 rounded border bg-white text-sm hover:bg-slate-50"
>
  Files
</button>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-3 py-1.5 rounded border bg-white text-sm hover:bg-slate-50"
          >
            Back
          </button>
        </div>
      </div>

      {errorText ? (
        <div className="shrink-0 border-b bg-red-50 text-red-700 px-3 py-2 text-sm">
          {errorText}
        </div>
      ) : null}

      <div className="flex-1 min-h-0 overflow-hidden">
        <ItemComponentsEditor
  item={item}
  setItem={setItem}
  itemMounting={itemMounting}
  itemMountingItems={itemMountingItems}
  onSaveItemPatch={saveItemPatch}
  components={components}
  setComponents={setComponents}
  assets={assets}
  componentItems={componentItems}
  assetSearch={assetSearch}
  setAssetSearch={setAssetSearch}
  componentCategories={componentCategories}
  parameterDefinitions={parameterDefinitions}
  fullScreen
  onReload={reloadComponents}
/>
      </div>

      {showProductImport ? (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
    <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded bg-white shadow-xl flex flex-col">
      <div className="h-12 border-b px-4 flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">Import Product Details</div>
          <div className="text-xs text-slate-500">
            Paste copied product details text from supplier document.
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowProductImport(false)}
          className="px-3 py-1.5 border rounded text-sm"
        >
          Close
        </button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-2 gap-4 p-4 overflow-hidden">
        <div className="min-h-0 flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">
            Pasted text
          </label>
          <textarea
            value={productImportText}
            onChange={(e) => setProductImportText(e.target.value)}
            className="flex-1 min-h-[500px] border rounded p-3 font-mono text-xs"
            placeholder="Paste Product details text here..."
          />

          <button
            type="button"
            onClick={parseProductImport}
            className="mt-3 px-3 py-2 rounded bg-blue-600 text-white text-sm"
          >
            Parse
          </button>
        </div>

        <div className="min-h-0 flex flex-col">
          <label className="text-xs font-medium text-slate-500 mb-1">
            Parsed result
          </label>

          <pre className="flex-1 min-h-[500px] border rounded bg-slate-50 p-3 overflow-auto text-xs">
            {parsedProductData
              ? JSON.stringify(parsedProductData, null, 2)
              : "No parsed data yet."}
          </pre>

          <button
            type="button"
            onClick={applyProductImport}
            disabled={!parsedProductData}
            className="mt-3 px-3 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-40"
          >
            Apply to item
          </button>
        </div>
      </div>
    </div>
  </div>
) : null}

{showFilesPanel ? (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
    <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded bg-white shadow-xl flex flex-col">
      <div className="h-12 border-b px-4 flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">Item Files / Documents</div>
          <div className="text-xs text-slate-500">
            Upload and manage product images, PDFs, manuals and technical files.
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowFilesPanel(false)}
          className="px-3 py-1.5 border rounded text-sm"
        >
          Close
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4">
        <ItemFilesPanel
          itemFiles={itemFiles}
          fileRole={fileRole}
          setFileRole={setFileRole}
          fileLabel={fileLabel}
          setFileLabel={setFileLabel}
          uploadingFile={uploadingFile}
          onUpload={handleUploadItemFile}
          onDelete={deleteItemFile}
        />
      </div>
    </div>
  </div>
) : null}

      <div className="h-6 shrink-0 border-t bg-white px-3 flex items-center justify-between text-[11px] text-slate-500">
        <div>Components: {components.length}</div>
        <div>MODULAR OBJECTS LLC · Item Assembly Editor</div>
      </div>
    </div>
  );
}